import { computed, ref } from 'vue'
import { fetchAdminSession, loginAdmin, logoutAdmin } from '@/api/admin'
import {
  ADMIN_MIN_ROLE_LEVEL,
  SUPER_ADMIN_ROLE_LEVEL,
  clearAdminSession,
  loadAdminSession,
  saveAdminSession,
  type AdminSession,
} from '@/constants/adminSession'
import { saveAutoLogin } from '@/constants/autoLogin'

const admin = ref<AdminSession | null>(loadAdminSession())
const initialized = ref(false)
const initializing = ref(false)
/** 헤더와 라우터가 동시에 세션을 물어보면, 먼저 호출한 쪽만 기다리고 나머지는 빈 계정으로 로그인 화면을 연다. */
let pendingInit: Promise<void> | null = null

export function useAdminAuth() {
  const isAdminLoggedIn = computed(() => Boolean(admin.value))
  /** 최고관리자만 열어야 하는 기능이 생기면 이 값을 본다. */
  const isSuperAdmin = computed(() => (admin.value?.roleLevel ?? 0) >= SUPER_ADMIN_ROLE_LEVEL)

  const setAdmin = (next: AdminSession | null) => {
    admin.value = next
    if (next) {
      saveAdminSession(next)
      return
    }
    clearAdminSession()
  }

  const initAdminAuth = () => {
    if (initialized.value) {
      return Promise.resolve()
    }
    if (pendingInit) {
      return pendingInit
    }

    initializing.value = true
    pendingInit = (async () => {
      try {
        setAdmin(await fetchAdminSession())
      } catch {
        setAdmin(null)
      } finally {
        initialized.value = true
        initializing.value = false
        pendingInit = null
      }
    })()

    return pendingInit
  }

  const login = async (userId: string, password: string, rememberMe = false) => {
    const result = await loginAdmin({ userId, password, rememberMe })

    if (result.roleLevel < ADMIN_MIN_ROLE_LEVEL) {
      throw new Error('관리자 권한이 있는 계정만 들어올 수 있습니다.')
    }

    saveAutoLogin('admin', rememberMe)
    setAdmin(result)
    initialized.value = true
    return result
  }

  const logout = async () => {
    try {
      await logoutAdmin()
    } finally {
      saveAutoLogin('admin', false)
      setAdmin(null)
      initialized.value = true
    }
  }

  return {
    admin,
    isAdminLoggedIn,
    isSuperAdmin,
    initialized,
    initAdminAuth,
    login,
    logout,
    setAdmin,
  }
}
